import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useStableAuth } from './useStableAuth';
import { toast } from 'sonner';
import type { TaskSection } from '@/types/task';

export const useTaskSections = () => {
  const { userId, companyId } = useStableAuth();
  const [sections, setSections] = useState<TaskSection[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchSections = async () => {
    if (!userId || !companyId) return;

    try {
      // Buscar seções da empresa (não mais por usuário - modelo simplificado)
      const { data, error } = await supabase
        .from('task_sections')
        .select(`
          *,
          member_count:task_section_members(count)
        `)
        .eq('company_id', companyId)
        .eq('active', true)
        .order('display_order', { ascending: true });

      if (error) throw error;
      setSections((data || []) as TaskSection[]);
    } catch (error) {
      console.error('Erro ao carregar seções:', error);
      toast.error('Erro ao carregar seções');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSections();
  }, [userId, companyId]);

  const createSection = async (name: string, description?: string, color?: string, visibility?: string, allowJoinRequests?: boolean) => {
    if (!userId || !companyId) return;

    try {
      const maxOrder = sections.length > 0 
        ? Math.max(...sections.map(s => s.display_order))
        : -1;

      const { data, error } = await supabase
        .from('task_sections')
        .insert([{
          company_id: companyId,
          user_id: userId,
          name,
          description,
          color: color || '#3b82f6',
          display_order: maxOrder + 1,
          visibility: visibility || 'private',
          allow_join_requests: allowJoinRequests || false
        }])
        .select()
        .single();

      if (error) throw error;
      
      toast.success('Seção criada com sucesso');
      await fetchSections();
      return data;
    } catch (error) {
      console.error('Erro ao criar seção:', error);
      toast.error('Erro ao criar seção');
      throw error;
    }
  };

  const updateSection = async (
    sectionId: string, 
    updates: Partial<Omit<TaskSection, 'id' | 'company_id' | 'user_id' | 'created_at' | 'updated_at'>>
  ) => {
    try {
      const { error } = await supabase
        .from('task_sections')
        .update(updates)
        .eq('id', sectionId);

      if (error) throw error;
      
      toast.success('Seção atualizada com sucesso');
      await fetchSections();
    } catch (error) {
      console.error('Erro ao atualizar seção:', error);
      toast.error('Erro ao atualizar seção');
      throw error;
    }
  };

  const deleteSection = async (sectionId: string) => {
    try {
      // Verificar se é seção padrão
      const section = sections.find(s => s.id === sectionId);
      if (section?.is_default) {
        toast.error('Não é possível excluir a seção "Entrada de Tarefas". Esta seção é usada pelo sistema para receber tarefas.');
        return;
      }

      const { error } = await supabase
        .from('task_sections')
        .update({ active: false })
        .eq('id', sectionId);

      if (error) throw error;
      
      toast.success('Seção excluída com sucesso');
      await fetchSections();
    } catch (error) {
      console.error('Erro ao excluir seção:', error);
      toast.error('Erro ao excluir seção');
      throw error;
    }
  };

  return {
    sections,
    loading,
    createSection,
    updateSection,
    deleteSection,
    refetch: fetchSections
  };
};
